"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, Tag } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import { getAllCategories, Category as CategoryType } from '@/lib/supabase';

interface CategorySelectorProps {
  selectedCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  disabled?: boolean;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategoryId,
  onCategoryChange,
  disabled,
}) => {
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedCategories = await getAllCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast({
        title: "Error al cargar categorías",
        description: "No se pudieron cargar las categorías de notas.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <div className="grid gap-2">
      <Label htmlFor="note-category" className="flex items-center gap-2">
        <Tag className="h-3 w-3 sm:h-4 sm:w-4" /> Categoría
      </Label>
      <Select
        value={selectedCategoryId || ''}
        onValueChange={(value) => onCategoryChange(value === '' ? null : value)}
        disabled={disabled || loading}
      >
        <SelectTrigger id="note-category">
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando categorías...
            </span>
          ) : (
            <SelectValue placeholder="Selecciona una categoría" />
          )}
        </SelectTrigger>
        <SelectContent>
          {categories.map((category) => (
            <SelectItem key={category.id} value={category.id}>
              {category.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default CategorySelector;